import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Subject, Subscription, takeUntil } from 'rxjs'
import { ACCAuthenticationService } from '../../../services/acc-auth.service'
import { TooltipPosition } from '../../directives/tooltip.directive'
import { ModalController } from '@ionic/angular'
import { Translation } from '../../../services/translation.service'
import { accAuthConfig } from '../../../../environments/environment'
import { Location } from '@angular/common'
import { ModalComponent, ModalDismissAction } from '@doka-shared/common'

@Component({
  selector: 'efp-acc-settings',
  templateUrl: './acc-settings.component.html',
  styleUrls: ['./acc-settings.component.scss'],
})
export class AccSettingsComponent implements OnInit, OnDestroy {
  private readonly pageLeave$ = new Subject<void>()
  private queryParamsSubscription: Subscription | undefined
  public accMail = ''
  public accConnectorEnabled$ = this.accAuthService.connectorEnabled$
  public tooltipPosition = TooltipPosition

  constructor(
    private readonly accAuthService: ACCAuthenticationService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly router: Router,
    private readonly translate: Translation,
    private readonly modalCtrl: ModalController,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.queryParamsSubscription?.unsubscribe()
    this.queryParamsSubscription = this.activatedRoute.queryParams
      .pipe(takeUntil(this.pageLeave$))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async (params) => {
        if (Object.keys(params).length > 0 && 'code' in params) {
          // call Auth endpoint
          this.accAuthService.createAccToken(params.code)

          this.deleteQueryParam()
        }
      })
  }

  ngOnDestroy(): void {
    this.pageLeave$.next()
  }

  public async connectToAcc(): Promise<void> {
    window.location.href =
      accAuthConfig.url +
      `?response_type=code` +
      `&client_id=` +
      accAuthConfig.client_id +
      `&redirect_uri=` +
      window.document.URL +
      `&scope=` +
      accAuthConfig.scope
  }

  public getAccUserMail(): string {
    return this.accAuthService.getAccMail()
  }

  private deleteQueryParam(): void {
    // Get the current URL without query parameters
    const urlWithoutParams = this.location.path().split('?')[0]

    // Update the URL without reloading
    this.location.replaceState(urlWithoutParams)
  }

  public async unlinkAccAccount(): Promise<void> {
    await this.accAuthService.logout()
  }

  public async unlinkAlert(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      backdropDismiss: false,
      cssClass: 'small-modal',
      componentProps: {
        title: this.translate.translate('ACC.DISCONNECT_TITLE'),
        text: this.translate.translate('ACC.DISCONNECT_TEXT'),
        cancelButtonLabel: this.translate.translate('GENERAL.CANCEL'),
        confirmButtonLabel: this.translate.translate('GENERAL.OK'),
      },
    })
    await modal.present()
    const { role } = await modal.onDidDismiss()

    if (role === ModalDismissAction.CONFIRM) {
      await this.unlinkAccAccount()
    }
  }

  getAuthText(checkIfConnectorEnabled: boolean | null): string {
    if (checkIfConnectorEnabled) return this.translate.translate('ACC.AUTHORIZED')
    else return this.translate.translate('ACC.UNAUTHORIZED')
  }
}
